"use client";

import type { ReactNode } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Filters } from "@/components/ui/filters";
import { Icon, type IconName } from "@/components/ui/icon";
import { useTalentSearchFilters } from "@/components/search/talent-search-filters-provider";
import { SCOPE_OPTIONS } from "@/features/search/lib/filter-options";
import { useSectionUnavailableReason } from "./pool-category-gate";

export {
  FilterLabel,
  ClearChipsButton,
  FilterFieldContainer,
  FilterFieldLabel,
  FilterFieldContent,
  // Talent Search always shows the required/optional asterisk, so every call
  // site here passes `required` + `onToggleRequired`; both are optional on the
  // shared chip for panels (like the job pipeline) without that concept.
  FilterChip,
  FilterTag,
} from "@/components/ui/filters";

export function BulkModifyDropdown({
  onSetScope,
  onExcludeAll,
  onUnexcludeAll,
  onRequireAll,
  onUnrequireAll,
}: {
  onSetScope?: (scope: "c" | "p" | "cp") => void;
  onExcludeAll?: () => void;
  onUnexcludeAll?: () => void;
  onRequireAll?: () => void;
  onUnrequireAll?: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1 cursor-pointer group shrink-0"
        >
          <div className="opacity-40 group-hover:opacity-60 transition-opacity">
            <Icon name="IconEditBig" size={13} className="text-heading" />
          </div>
          <span className="text-[13px] font-normal text-contrast/50 group-hover:text-contrast/70 transition-colors">
            Bulk Modify
          </span>
          <div className="opacity-40">
            <Icon
              name="IconChevronDownSmall"
              size={12}
              className="text-heading"
            />
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={4} className="w-[210px]">
        {onSetScope &&
          SCOPE_OPTIONS.map((opt) => (
            <DropdownMenuItem
              key={opt.value}
              onSelect={() => onSetScope(opt.value)}
            >
              <Icon
                name={opt.icon}
                size={14}
                className="text-foreground opacity-70"
              />
              <span>Set all to {opt.label}</span>
            </DropdownMenuItem>
          ))}
        {(onRequireAll || onUnrequireAll) && onSetScope && (
          <DropdownMenuSeparator />
        )}
        {onRequireAll && (
          <DropdownMenuItem onSelect={onRequireAll}>
            <Icon name="IconAsterisk" size={14} className="text-ai" />
            <span>Mark all required</span>
          </DropdownMenuItem>
        )}
        {onUnrequireAll && (
          <DropdownMenuItem onSelect={onUnrequireAll}>
            <Icon
              name="IconAsterisk"
              size={14}
              className="text-foreground opacity-40"
            />
            <span>Mark all optional</span>
          </DropdownMenuItem>
        )}
        {((onRequireAll || onUnrequireAll) &&
          (onExcludeAll || onUnexcludeAll)) ||
        (onSetScope &&
          (onExcludeAll || onUnexcludeAll) &&
          !(onRequireAll || onUnrequireAll)) ? (
          <DropdownMenuSeparator />
        ) : null}
        {onExcludeAll && (
          <DropdownMenuItem onSelect={onExcludeAll}>
            <Icon
              name="IconCircleBanSign"
              size={14}
              className="text-destructive dark:text-red-400 opacity-70"
            />
            <span>Exclude All</span>
          </DropdownMenuItem>
        )}
        {onUnexcludeAll && (
          <DropdownMenuItem onSelect={onUnexcludeAll}>
            <Icon
              name="IconStepBack"
              size={14}
              className="text-foreground opacity-70"
            />
            <span>Un-exclude All</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function PresetButton({
  label,
  onClick,
}: {
  label: string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      className="relative flex items-center justify-center gap-1.5 h-7 px-2 w-full text-xs font-medium text-[#8a5200] dark:text-ai border border-[#8a5200]/20 dark:border-ai/10 rounded-b-md rounded-t-none overflow-hidden cursor-pointer before:absolute before:inset-0 before:bg-gradient-to-t before:from-[#ffaf53]/10 dark:before:from-ai/10 before:to-transparent dark:before:to-ai/3 before:pointer-events-none before:transition-opacity hover:before:opacity-75 dark:hover:before:opacity-50"
    >
      <svg
        aria-hidden="true"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="relative z-[1] shrink-0"
      >
        <path
          d="M20.25 3.75H21V3H20.25V3.75ZM20.25 20.25V21H21V20.25H20.25ZM3.75 20.25H3V21H3.75V20.25ZM3.75 3.75V3H3V3.75H3.75ZM19.5 3.75V20.25H21V3.75H19.5ZM20.25 19.5H3.75V21H20.25V19.5ZM4.5 20.25V3.75H3V20.25H4.5ZM3.75 4.5H20.25V3H3.75V4.5Z"
          fill="currentColor"
        />
        <path
          d="M12 13.5H11.25V15H12V13.5ZM20.25 15H21V13.5H20.25V15ZM12 15H20.25V13.5H12V15Z"
          fill="currentColor"
        />
        <path
          d="M12 10.5H12.75V9H12V10.5ZM3.75 9H3V10.5H3.75V9ZM12 9H3.75V10.5H12V9Z"
          fill="currentColor"
        />
        <path
          d="M11.25 20.25V21H12.75V20.25H11.25ZM12.75 3.75V3H11.25V3.75H12.75ZM11.25 3.75V20.25H12.75V3.75H11.25Z"
          fill="currentColor"
        />
      </svg>
      <span className="relative z-[1]">{label}</span>
    </button>
  );
}

// Thin wrapper around the shared `<Filters.Section />` that binds open/close
// to the Talent Search context-backed `openSections` map. Keeps the existing
// per-section call-site API (sectionKey + icon + label + activeCount + onClear)
// unchanged.
export function FilterSection({
  sectionKey,
  icon,
  label,
  activeCount,
  onClear,
  children,
}: {
  sectionKey: string;
  icon: IconName;
  label: string;
  activeCount?: number;
  onClear?: () => void;
  children: ReactNode;
}) {
  const { openSections, toggleSection } = useTalentSearchFilters();
  const open = openSections[sectionKey] ?? false;
  // A category the selected pools don't support: the inputs are replaced by
  // the reason. Count and Clear stay so a value saved before a pool turned
  // the category off can still be removed.
  const unavailable = useSectionUnavailableReason(sectionKey);

  return (
    <Filters.Section
      icon={icon}
      label={label}
      open={open}
      onOpenChange={(next) => {
        if (next !== open) toggleSection(sectionKey);
      }}
      activeCount={activeCount}
      onClear={onClear}
    >
      <Filters.SectionContent>
        {unavailable ? (
          <p data-testid={`filter-unavailable-${sectionKey}`} className="text-xs text-contrast/60">
            {unavailable}
          </p>
        ) : (
          children
        )}
      </Filters.SectionContent>
    </Filters.Section>
  );
}
